<template>
    <div v-if="opened" v-on-clickaway="closeDatePicker" :class="['datepicker', dateClass]">
        <div class="datepicker-title">
            <span class="prev-month" @click.prevent="prevMonth()"><i class="icon-chewron-down"></i></span>
            <span class="title">{{ monthName }} {{ currentYear }}</span>
            <span class="next-month" @click.prevent="nextMonth()"><i class="icon-chewron-down"></i></span>
        </div>
        <div class="dayweek">
            <span v-for="weekDay in weekDays" :key="weekDay">{{ weekDay }}</span>
        </div>
        <span
            v-for="(date, index) in currentCalendarView"
            :key="index"
            :class="['datecell', setClass(date)]"
            @click="update(date)"
            >{{ day(date) }}
        </span>
        <div class="datepicker-today" @click.prevent="setToday()">{{ $t('common.today') }}</div>
    </div>
</template>

<script type="text/babel">
import { mixin as clickaway } from 'vue-clickaway';
import getDate from 'date-fns/getDate';
import getMonth from 'date-fns/getMonth';
import addMonths from 'date-fns/addMonths';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import getYear from 'date-fns/getYear';
import addDays from 'date-fns/addDays';
import isToday from 'date-fns/isToday';
import isSameMonth from 'date-fns/isSameMonth';
import isEqual from 'date-fns/isEqual';

import { MONTHS, SHORTWEEKDAYS } from '../../../constants';
import set from 'date-fns/set';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';

export default {
    mixins: [clickaway],

    props: {
        value: {
            required: true,
            validator: value => {
                return value === null || value instanceof Date || value === '';
            },
        },
        dateClass: {
            required: false,
            type: String,
            default: '',
        },

        opened: {
            required: true,
            type: Boolean,
        },
    },
    data() {
        return {
            currentDate: null,
        };
    },

    computed: {
        /**
         * @returns {array} of week days
         */
        weekDays() {
            return SHORTWEEKDAYS;
        },

        /**
         * Get current month name
         * @return {string}
         */
        monthName() {
            return MONTHS[getMonth(this.currentDate)];
        },

        currentYear() {
            return getYear(this.currentDate);
        },

        /**
         * Write to the array the current month, also looks at the previous and next months to fill the view for
         * 6 full weeks
         * @return {Array} of dates
         */
        currentCalendarView() {
            let currentView = [];
            let firstCalDay = startOfWeek(startOfMonth(this.currentDate), { weekStartsOn: 1 });
            for (let d = 0; d < 42; d++) {
                currentView.push(addDays(firstCalDay, d));
            }
            return currentView;
        },
    },

    watch: {
        opened() {
            this.setCurrentDate();
        },
        $route() {
            this.closeDatePicker();
        },
    },

    created() {
        this.setCurrentDate();
    },

    methods: {
        // Set previous month
        prevMonth() {
            this.currentDate = addMonths(this.currentDate, -1);
        },

        // Set next month
        nextMonth() {
            this.currentDate = addMonths(this.currentDate, 1);
        },

        setToday() {
            this.update(new Date());
        },

        closeDatePicker() {
            this.$emit('close', false);
        },

        update(date) {
            this.$emit('checkvalid');
            // Update only date and keep time as it was
            let lDate =
                this.value instanceof Date
                    ? set(date, {
                          hours: getHours(this.value),
                          minutes: getMinutes(this.value),
                          seconds: 0,
                          milliseconds: 0,
                      })
                    : date;
            this.$emit('input', lDate);
            this.closeDatePicker();
        },

        /**
         * Watch current view and set classes for datepicker cells.
         * @param date
         * @return {string}
         */
        setClass(date) {
            if (this.value) {
                if (isEqual(this.value, date)) {
                    return 'datecell-selected';
                }
            }

            if (!isSameMonth(date, this.currentDate)) {
                return 'not-current-month';
            }

            if (isToday(date)) {
                return 'current-day';
            }
        },

        day(date) {
            return getDate(date);
        },

        /**
         * Set current date
         */
        setCurrentDate() {
            this.currentDate = new Date();
        },
    },
};
</script>
